import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { StarIcon, TagIcon } from '../icons'

const t = buildTranslate('promo_codes.overview.comparison')

const ROW_KEYS = [
  'who_it_reaches',
  'best_for',
  'distribution',
  'attribution',
  'security',
] as const

const CARDS = [
  {
    key: 'promo_codes',
    accent: palette.belizeHole,
    badgeBackground: palette.belizeHole10,
    subtitle: palette.belizeHole,
    Icon: TagIcon,
  },
  {
    key: 'loyalty_rewards',
    accent: palette.spearmint50,
    badgeBackground: palette.spearmint10,
    // Figma darkens the subtitle to #0E9B99 so small teal text keeps contrast
    // on white; spearmint60 is the nearest palette token.
    subtitle: palette.spearmint60,
    Icon: StarIcon,
  },
] as const

const CARD_ICON_SIZE = 17
const CARD_ICON_BADGE = 40

const Comparison = () => {
  const [css] = useStyletron()

  return (
    <section data-testid="promo-codes-comparison">
      <Text.Header3 tag="h2" color="grey90" className="mt-0 mb-m">
        {t('title')}
      </Text.Header3>
      {/* The two cards share one grid and re-use its rows via `subgrid`, so a
          value that wraps to two lines in one card grows the matching row in
          the other. Without it the "Who it reaches"/"Best for"/… labels drift
          out of alignment, which is the whole point of the comparison. */}
      <div
        className={css({
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gridTemplateRows: `repeat(${ROW_KEYS.length + 1}, auto)`,
          gap: '24px',
        })}
      >
        {CARDS.map(card => (
          <div
            key={card.key}
            data-testid={`comparison-card-${card.key}`}
            className={css({
              display: 'grid',
              gridRow: `span ${ROW_KEYS.length + 1}`,
              gridTemplateRows: 'subgrid',
              // A subgrid inherits the parent's gutters unless it sets its own.
              // The 24px above is the gutter between the two cards; inherited,
              // it would also space out the rows inside them.
              rowGap: 0,
              backgroundColor: palette.white,
              // Longhands only: styletron emits shorthand and longhand as
              // separate atomic classes, so a `border` + `borderTop` pair
              // resolves by stylesheet order rather than by what's written.
              borderTop: `3px solid ${card.accent}`,
              borderRight: `1px solid ${palette.grey20}`,
              borderBottom: `1px solid ${palette.grey20}`,
              borderLeft: `1px solid ${palette.grey20}`,
              borderRadius: '8px',
              overflow: 'hidden',
            })}
          >
            <div
              className={`p-m ${css({
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
              })}`}
            >
              <span
                className={css({
                  flexShrink: 0,
                  width: `${CARD_ICON_BADGE}px`,
                  height: `${CARD_ICON_BADGE}px`,
                  borderRadius: '8px',
                  backgroundColor: card.badgeBackground,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                })}
              >
                <card.Icon size={CARD_ICON_SIZE} color={card.accent} />
              </span>
              <div>
                <Text.Header4 tag="h3" color="grey90" className="mt-0 mb-xxs">
                  {t(`${card.key}.title`)}
                </Text.Header4>
                <div className={css({ color: card.subtitle })}>
                  <Text.BodyText4 bold color="inherit" tag="div">
                    {t(`${card.key}.subtitle`)}
                  </Text.BodyText4>
                </div>
              </div>
            </div>
            <dl
              className={`m-0 ${css({
                display: 'grid',
                gridRow: `span ${ROW_KEYS.length}`,
                gridTemplateRows: 'subgrid',
                rowGap: 0,
              })}`}
            >
              {ROW_KEYS.map(rowKey => (
                <div
                  key={rowKey}
                  className={`px-m py-s ${css({
                    display: 'flex',
                    gap: '16px',
                    borderTop: `1px solid ${palette.grey20}`,
                  })}`}
                >
                  <dt className={css({ flex: '0 0 120px' })}>
                    <Text.SmallCaps3 bold color="grey50" tag="span">
                      {t(`rows.${rowKey}`)}
                    </Text.SmallCaps3>
                  </dt>
                  <dd className={`m-0 ${css({ flex: '1 1 auto' })}`}>
                    <Text.BodyText4 color="grey90" tag="span">
                      {t(`${card.key}.${rowKey}`)}
                    </Text.BodyText4>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <Text.BodyText4 color="grey70" tag="p" className="mt-m mb-0">
        {t('guidance')}
      </Text.BodyText4>
    </section>
  )
}

export default Comparison
